import { ReactElement, ReactNode } from 'react';
import _ from "lodash";
import type { LocalizationMap } from '@Primitive/Localization/Localization';
import type { WebModule } from '@API/WebModule';
import { IWidgetDescription, IWidgetGroupDesc, WidgetThingGenericConfiguration, IWidgetClass, WidgetGroups, IWidgetDataValidateResp } from './WidgetStructs';
import type { IWidgetThings } from './Things/WidgetsThingsStructs';
import type { IWidgetDialog } from './IWidgetClassParent';
import * as WidgetThingsLib from "./Things/index";
import { WidgetDescriptor } from '@ModelViews/ModelViews';
import { grey600 } from '@Utils/Colors';
import { yHas } from '@Utils/Utils';
// ================================================================================================
class NullWidget extends IWidgetClass<any> {
    public NodeKey: string;
    public ThingKey: string;
    public WebModuleKey: string;
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): ReactNode {
        return <div style={{ width, height, lineHeight: height + "px", fontSize: 24, fontWeight: 700, textAlign: "center" }}>Not Supported Widget</div>;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderBase(key: string, width: number, height: number): ReactNode {
        return this.Render(key, width, height);
    }
    // ------------------------------------------------------------------------------------------------
    public RenderToolbar(key: string): ReactNode { return null; }
    // ------------------------------------------------------------------------------------------------
    public ToolbarDynamicTooltip(): string | ReactNode[] { return null; }
    // ------------------------------------------------------------------------------------------------
    public RenderBaseToolbar(key: string): ReactNode[] { return null; }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): ReactNode { return null; }
    // ------------------------------------------------------------------------------------------------
    public RenderBaseSettings(key: string, width: number, height: number, dialog: IWidgetDialog,
        onSaveSettings: (settings: WidgetThingGenericConfiguration) => void): ReactNode {
        return this.RenderWidgetSettings(key, width, height);
    }
    // ------------------------------------------------------------------------------------------------
    WidgetDataUpdate(NewWidgetData: any) { }
    GetDefaultWidgetData(): any { return {}; }
    MigrateDefaultWidgetData(data: any): any { return data; }
    GetDefaultValues(): { [key: string]: any } { return {}; }
    GetDefaultWidgetDataStatic(): any { return {}; }
    ValidateValues(key: string, value: any): IWidgetDataValidateResp { return { valid: true, errors: "" } };
    GetWidgetDesc(): IWidgetDescription { return null; };
    GetLocalizationDictionary(): LocalizationMap { return {} };
    UpdateLocalization() { }
    GetLocalizedWidgetTitle() { return null; }
    GetDataPortKeyStatic() { return null; }
    // ------------------------------------------------------------------------------------------------
}
// ================================================================================================
export default class Factory {
    private static _groupDesc: IWidgetGroupDesc[] = null;
    private static _groupTypeToWidget: {
        [type: string]: {
            Group: string,
            Name: string,
            WidgetDesc: IWidgetDescription
        }
    } = null;
    // ------------------------------------------------------------------------------------------------
    public static CreateWidgetThing(desc: IWidgetDescription, data: any, webModule: WebModule): IWidgetThings {
        if (yHas(WidgetThingsLib, desc.Type)) {
            return new WidgetThingsLib[desc.Type](desc, data, webModule);
        } else {
            return new NullWidget(desc, data);
        }
    }
    // ------------------------------------------------------------------------------------------------
    /**
     * Get description of the widget
     * @param type 
     * @returns Widget description
     */
    public static CreateWidgetThingDescDefault(type: string): IWidgetDescription {
        if (!yHas(WidgetThingsLib, type))
            return null;

        return WidgetThingsLib[type].GetWidgetDesc();
    }
    // ------------------------------------------------------------------------------------------------
    /**
     * Get description with data
     * @param type 
     * @returns Widget description with sample data
     */
    public static CreateWidgetThingDesc(type: string): IWidgetDescription {
        if (!yHas(WidgetThingsLib, type))
            return null;

        let desc = WidgetThingsLib[type].GetWidgetDesc();
        desc.Data = _.cloneDeep(WidgetThingsLib[type].GetDefaultWidgetDataStatic());
        return desc;
    }
    // ------------------------------------------------------------------------------------------------
    public static CommonCreateWidgetThingFromType(
        widgetType: string,
        webModule: WebModule
    ): IWidgetThings {
        // note that `CreateWidgetThingDescDefault` is used
        let widgetDesc = Factory.CreateWidgetThingDescDefault(widgetType);

        return this.CommonCreateWidgetThing(widgetDesc, webModule);
    }
    // ------------------------------------------------------------------------------------------------
    public static CommonCreateWidgetThing(
        widgetDesc: WidgetDescriptor,
        webModule: WebModule
    ): IWidgetThings {

        // fix Key (if needed)
        if (widgetDesc.Key.indexOf("_") === -1)
            widgetDesc.Key = widgetDesc.Key + "_" + Math.round(Math.random() * 1000);

        let widget = Factory.CreateWidgetThing(widgetDesc, widgetDesc.Data, webModule);

        // copy `Description`
        widget.Description = widgetDesc;

        // get `DefaultValues` (if empty)
        if (_.keys(widget.Description.DefaultValues).length == 0)
            widget.Description.DefaultValues = widget.GetDefaultValues();

        return widget;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetTypeToWidget() {
        if (this._groupTypeToWidget == null) {
            this.CreateWidgetGroup();
        }
        return this._groupTypeToWidget;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetGroups() {
        if (this._groupDesc == null) {
            this.CreateWidgetGroup();
        }
        return this._groupDesc;
    }
    // ------------------------------------------------------------------------------------------------
    public static WidgetCostWeights(): { [key: string]: number } {
        return {
            // Control type widgets
            "SwitchWidget": 1,
            "ButtonsWidget": 1,
            "CommonListWidget": 1,

            // User input widgets
            "ColorPaletteWidget": 1,
            "DatePickerWidget": 1,
            "FormWidget": 4,
            "QueryStructureBuilderWidget": 4,
            "DynamicFormWidget": 4,
            "GroupOfButtonGroupsWidget": 4,

            // View type widgets
            "IFrameWidget": 1,
            "LabelWidget": 1,
            "MarkdownWidget": 1,
            "TableWidget": 4,
            "MapsWidget": 4,
            "TimeLineWidget": 4,
            "TileWidget": 2,
            "StatTileWidget": 2,
            "AssetTrackerWidget": 4,
            "DecorationLabelWidget": 1,
            "JsonViewerWidget": 2,
            "AdvanceInfoGridWidget": 4,
            "OdometerWidget": 2,
            "TreeWidget": 2,
            "CalendarWidget": 4,
            "InfoTipCoreWidget": 2,
            "HeatmapWidget": 4,
            "QRCodeViewerWidget": 2,
            "CalendarV2Widget": 4,

            // Image type widgets
            "ImageViewerWidget": 4,
            "CVViewerWidget": 4,
            "VideoPlayerWidget": 4,
            "CameraWidget": 4,
            "BarcodeScannerWidget": 4,
            "ForgeViewerWidget": 4,
            "MatterportWidgetWidget": 4,

            // Charts type widgets
            "BubbleChartWidget": 3,
            "BarChartWidget": 3,
            "PieChartWidget": 3,
            "DoughnutChartWidget": 3,
            "PolarAreaChartWidget": 3,
            "LineChartWidget": 3,

            // Gauges type widgets
            "RadialGaugeWidget": 2,
            "LinearGaugeWidget": 2,
            "KnobWidget": 2,
            "GaugeLabelWidget": 2,
            "PercentageGaugeWidget": 2,

            // Singleton type widgets
            "LedWidget": 1,
            "ButtonSingletonWidget": 1,
            "LabelSingletonWidget": 1,

            // Layout type widgets
            "LayoutTabsWidget": 0,
            "BlankWidget": 0,
            "PagePortalWidget": 0,
            "DynamicGridWidget": 0,
            "DynamicPageWidget": 0
        }
    }
    // ------------------------------------------------------------------------------------------------
    public static CreateWidgetGroup(): IWidgetGroupDesc[] {

        if (this._groupDesc != null)
            return this._groupDesc;
        let group: IWidgetGroupDesc[] = [];

        const groupsInfo: {
            [key: string]: {
                Description: string,
                Icon: ReactElement,
                WidgetTypes: string[]
            }
        } = {
            // Control Group
            [WidgetGroups.control]: {
                Description: 'Control type widgets',
                Icon: <i className='fas fa-cog center' style={{ color: grey600 }} />,
                WidgetTypes: [
                    'SwitchWidget',
                    'ButtonsWidget',
                    'CommonListWidget']
            },

            // Input Group
            [WidgetGroups.userInput]: {
                Description: 'User input widgets',
                Icon: <i className='fas fa-sign-in center' style={{ color: grey600 }} />,
                WidgetTypes: [
                    'ColorPaletteWidget',
                    'DatePickerWidget',
                    'FormWidget',
                    'QueryStructureBuilderWidget',
                    'DynamicFormWidget',
                    'FileUploadWidget',
                    'GroupOfButtonGroupsWidget',
                    'CodeEditorWidget',
                    'DateRangePickerWidget'
                ]
            },

            // Views Group
            [WidgetGroups.view]: {
                Description: 'View type widgets',
                Icon: <i className='far fa-square center' style={{ color: grey600 }} />,
                WidgetTypes: [
                    'LabelWidget',
                    'IFrameWidget',
                    'MarkdownWidget',
                    'TableWidget',
                    'MapsWidget',
                    'TimeLineWidget',
                    'TileWidget',
                    'StatTileWidget',
                    'AssetTrackerWidget',
                    'DecorationLabelWidget',
                    'JsonViewerWidget',
                    'AdvanceInfoGridWidget',
                    'OdometerWidget',
                    'TreeWidget',
                    'CalendarWidget',
                    'InfoTipCoreWidget',
                    'HeatmapWidget',
                    'InfoCardsWidget',
                    'TextEditorWidget',
                    'QRCodeViewerWidget',
                    'CalendarV2Widget',
                    'TemplatingHandlebarsWidget',
                    'PdfViewerWidget',
                    'HierarchyViewerWidget'
                ]
            },

            // Image Group
            [WidgetGroups.image]: {
                Description: 'Image type widgets',
                Icon: <i className='fas fa-paint-brush center' style={{ color: grey600 }} />,
                WidgetTypes: [
                    'ImageViewerWidget',
                    'CVViewerWidget',
                    'VideoPlayerWidget',
                    'CameraWidget',
                    'BarcodeScannerWidget',
                    'ForgeViewerWidget',
                    'MatterportWidgetWidget']
            },

            // Charts Group
            [WidgetGroups.charts]: {
                Description: 'Charts type widgets',
                Icon: <i className='fas fa-analytics center' style={{ color: grey600 }} />,
                WidgetTypes: [
                    'BubbleChartWidget',
                    'BarChartWidget',
                    'PieChartWidget',
                    'DoughnutChartWidget',
                    'PolarAreaChartWidget',
                    'LineChartWidget',
                    'amChartsWidget'
                ]
            },

            // Gauges Group
            [WidgetGroups.gauges]: {
                Description: 'Gauges type widgets',
                Icon: <i className='fas fa-tachometer center' style={{ color: grey600 }} />,
                WidgetTypes: [
                    'RadialGaugeWidget',
                    'LinearGaugeWidget',
                    'KnobWidget',
                    'GaugeLabelWidget',
                    'PercentageGaugeWidget']
            },

            // Singleton Group
            [WidgetGroups.singleton]: {
                Description: 'Singleton type widgets',
                Icon: <i className='fas fa-bookmark center' style={{ color: grey600 }} />,
                WidgetTypes: [
                    'LedWidget',
                    'ButtonSingletonWidget',
                    'LabelSingletonWidget']
            },

            // Layout Group
            [WidgetGroups.layout]: {
                Description: 'Layout type widgets',
                Icon: <i className='fas fa-border-all center' style={{ color: grey600 }} />,
                WidgetTypes: [
                    'LayoutTabsWidget',
                    'BlankWidget',
                    'PagePortalWidget',
                    'DynamicGridWidget',
                    'DynamicPageWidget'
                ]
            },

            // Custom Group
            [WidgetGroups.custom]: {
                Description: 'Custom widgets',
                Icon: <i className='fas fas fa-wrench center' style={{ color: grey600 }} />,
                WidgetTypes: [
                    'PlanogramEditorWidget',
                    'DemandPlanogramsEditorWidget',
                    'AmChartYearlyActivitiesWidget',
                    'AmChartMapWithTimelineWidget',
                    'AmChartSavingsWidget',
                    'QuestionnaireWidget',
                    'DeviceHierarchyWidget',
                    'SpaceDesignerWidget'
                ]
            }
        };

        // generate widget groups
        _.sortBy(_.keys(groupsInfo)).forEach((key) => {

            let widgetDesc: { [key: string]: IWidgetDescription } = {};
            let w: IWidgetDescription;
            _.forEach(_.sortBy(groupsInfo[key].WidgetTypes),
                (widgetType: string) => {
                    w = Factory.CreateWidgetThingDescDefault(widgetType);
                    widgetDesc[w.Key] = w;
                }
            );

            // Create group
            group.push({
                Name: key,
                Description: groupsInfo[key].Description,
                Widgets: widgetDesc,
                LeftIcon: groupsInfo[key].Icon
            });
        });

        this._groupTypeToWidget = {};
        _.forEach(group, (g) => {
            _.forOwn(g.Widgets, w => {
                this._groupTypeToWidget[w.Type] = {
                    Group: g.Name,
                    Name: w.Title,
                    WidgetDesc: w
                }
            });
        });

        this._groupDesc = group;
        return group;
    }
    // ------------------------------------------------------------------------------------------------
}
// ================================================================================================